import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { DESIGNS, MarketingPreview } from "../../components/MarketingDesignPreview";
import { toast } from "sonner";
import { Megaphone, Upload, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const statusColors = { paid: "bg-amber-50 text-amber-700 border-amber-200", in_production: "bg-blue-50 text-blue-700 border-blue-200", shipped: "bg-violet-50 text-violet-700 border-violet-200", completed: "bg-emerald-50 text-emerald-700 border-emerald-200" };

export default function Marketing() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [books, setBooks] = useState([]);
  const [sel, setSel] = useState(null);
  const [opts, setOpts] = useState({ size_id: "", material_id: "", quantity: 0, notes: "", project_id: "", design_id: "classic" });
  const [ship, setShip] = useState({ name: "", email: "", address1: "", address2: "", city: "", state: "", postal_code: "", country: "United States" });
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const pollRef = useRef(0);

  const load = () => {
    api.get("/marketing/catalog").then((r) => setCatalog(r.data));
    api.get("/marketing/my-orders").then((r) => setOrders(r.data));
    api.get("/projects").then((r) => setBooks(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    const poll = async () => {
      if (pollRef.current++ > 8) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Order confirmed! Upload your artwork below to start production.");
          setParams({});
          load();
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, setParams]);

  const open = (p) => {
    setSel(p);
    setOpts({ size_id: p.sizes[0].id, material_id: p.materials[0].id, quantity: p.tiers[0].qty, notes: "", project_id: "", design_id: "classic" });
  };

  const subtotal = () => {
    if (!sel) return 0;
    const s = sel.sizes.find((x) => x.id === opts.size_id);
    const m = sel.materials.find((x) => x.id === opts.material_id);
    const t = sel.tiers.find((x) => x.qty === Number(opts.quantity));
    return s && m && t ? Math.round(t.price * s.mult * m.mult * 100) / 100 : 0;
  };

  const shippingCost = () => {
    if (!sel?.shipping) return 0;
    const q = Number(opts.quantity) || 0;
    return Math.round((sel.shipping.base + sel.shipping.per_unit * Math.max(q - 1, 0)) * 100) / 100;
  };

  const price = () => Math.round((subtotal() + shippingCost()) * 100) / 100;

  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/checkout", {
        product_id: sel.id, size_id: opts.size_id, material_id: opts.material_id,
        quantity: Number(opts.quantity), notes: opts.notes, origin_url: window.location.origin, shipping: ship,
        project_id: opts.project_id, design_id: opts.project_id ? opts.design_id : "" });
      window.location.href = data.url;
    } catch (err) { toast.error(fmtErr(err)); setBusy(false); }
  };

  const uploadArtwork = async (order, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/marketing/orders/${order.id}/artwork`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Artwork uploaded — production can begin");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    e.target.value = "";
  };

  const setS = (k) => (e) => setShip((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="marketing-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading flex items-center gap-3"><Megaphone size={26} /> Marketing Materials</h1>
        <p className="text-neutral-500 mt-1">Professionally printed banners, bookmarks and flyers to promote your book.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {catalog.map((p) => (
          <div key={p.id} data-testid={`marketing-product-${p.id}`}
            className="bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col">
            <img src={p.image} alt={p.name} className="w-full h-44 object-cover" />
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-semibold font-heading">{p.name}</h3>
              <p className="text-xs text-neutral-500 mt-1.5 flex-1">{p.desc}</p>
              <p className="text-sm mt-3">from <span className="font-bold font-heading text-lg">${Math.min(...p.tiers.map((t) => t.price)).toFixed(0)}</span></p>
              <button data-testid={`marketing-order-${p.id}`} onClick={() => open(p)}
                className="mt-3 w-full py-2 rounded-full text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Configure & order</button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-lg font-semibold font-heading mb-4">My marketing orders</h2>
      {orders.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-2xl p-10 text-center text-neutral-500 text-sm" data-testid="marketing-orders-empty">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`marketing-order-row-${o.id}`} className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                <p className="font-semibold font-heading">{o.product_name} × {o.quantity}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{o.size_label} · {o.material_label} · #{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString()}</p>
                {o.design_name && (
                  <p className="text-xs text-neutral-500 mt-0.5" data-testid={`marketing-order-design-${o.id}`}>
                    Design: <b>{o.design_name}</b>{o.book_title ? <> — “{o.book_title}”</> : null}
                  </p>
                )}
              </div>
              <span className="font-semibold">${Number(o.total).toFixed(2)}
                {o.shipping_cost != null && <span className="block text-[10px] font-normal text-neutral-400">incl. ${Number(o.shipping_cost).toFixed(2)} shipping</span>}
              </span>
              <span className={`text-xs font-medium border rounded-full px-3 py-1 ${statusColors[o.status] || ""}`}>{o.status.replace(/_/g, " ")}</span>
              {o.artwork_filename ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 size={13} /> Artwork: {o.artwork_filename}</span>
              ) : (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50 cursor-pointer">
                  <Upload size={12} /> Upload artwork
                  <input data-testid={`artwork-upload-${o.id}`} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.ai,.psd,.zip" className="hidden" onChange={(e) => uploadArtwork(o, e)} />
                </label>
              )}
            </div>
          ))}
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4 overflow-y-auto" data-testid="marketing-config-modal">
          <form onSubmit={checkout} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-2xl w-full p-7 my-8">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold font-heading text-lg">{sel.name}</h3>
                <p className="text-sm text-neutral-500 mt-0.5">{sel.desc}</p>
              </div>
              <button type="button" data-testid="marketing-config-close" onClick={() => setSel(null)} className="text-neutral-400 hover:text-neutral-900">✕</button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              <div><label className="text-xs font-medium text-neutral-600">Size</label>
                <select data-testid="marketing-size-select" value={opts.size_id} onChange={(e) => setOpts((o) => ({ ...o, size_id: e.target.value }))} className={inputCls}>
                  {sel.sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-600">Material / stock</label>
                <select data-testid="marketing-material-select" value={opts.material_id} onChange={(e) => setOpts((o) => ({ ...o, material_id: e.target.value }))} className={inputCls}>
                  {sel.materials.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-600">Quantity</label>
                <select data-testid="marketing-qty-select" value={opts.quantity} onChange={(e) => setOpts((o) => ({ ...o, quantity: e.target.value }))} className={inputCls}>
                  {sel.tiers.map((t) => <option key={t.qty} value={t.qty}>{t.qty}</option>)}
                </select></div>
            </div>
            <textarea data-testid="marketing-notes-input" rows={2} value={opts.notes} onChange={(e) => setOpts((o) => ({ ...o, notes: e.target.value }))}
              className={inputCls + " mt-3"} placeholder="Design notes (book title, colors, text to include...)" />
            <hr className="my-4 border-neutral-100" />
            <p className="text-sm font-semibold">Personalize with your book cover <span className="text-neutral-400 font-normal">(optional)</span></p>
            {(() => {
              const book = books.find((b) => b.id === opts.project_id);
              const coverUrl = opts.project_id ? `${API}/api/store/cover/${opts.project_id}` : null;
              const design = DESIGNS.find((d) => d.id === opts.design_id) || DESIGNS[0];
              return (
                <div className="grid sm:grid-cols-[230px_1fr] gap-4 mt-3 items-start">
                  <div>
                    <label className="text-xs font-medium text-neutral-600">Your book</label>
                    <select data-testid="marketing-book-select" value={opts.project_id}
                      onChange={(e) => setOpts((o) => ({ ...o, project_id: e.target.value }))} className={inputCls}>
                      <option value="">No personalization</option>
                      {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
                    </select>
                    {opts.project_id && (
                      <>
                        <label className="text-xs font-medium text-neutral-600 mt-3 block">Design</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          {DESIGNS.map((d) => (
                            <button type="button" key={d.id} data-testid={`marketing-design-${d.id}`}
                              onClick={() => setOpts((o) => ({ ...o, design_id: d.id }))}
                              className={`rounded-lg border-2 p-1.5 transition-colors ${opts.design_id === d.id ? "border-neutral-900" : "border-neutral-200 hover:border-neutral-400"}`}>
                              <MarketingPreview productId={sel.id} design={d} coverUrl={coverUrl} title={book?.title} authorName={user?.name} mini />
                              <p className="text-[10px] font-medium mt-1 text-center">{d.name}</p>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {opts.project_id ? (
                    <div data-testid="marketing-design-preview" className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 flex flex-col items-center justify-center gap-2 min-h-[220px]">
                      <div className={sel.id === "hanging_banner" ? "w-full max-w-[380px]" : sel.id === "bookmarks" ? "w-24" : sel.id === "retractable_banner" ? "w-32" : "w-44"}>
                        <MarketingPreview productId={sel.id} design={design} coverUrl={coverUrl} title={book?.title} authorName={user?.name} />
                      </div>
                      <p className="text-[10px] text-neutral-400 text-center">Live preview — our print team refines the final layout using the “{design.name}” design.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-neutral-400 self-center leading-relaxed" data-testid="marketing-personalize-hint">
                      Choose one of your books to see your actual cover placed on this product. The design you pick is attached to your order for our print team.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="mt-4 text-right">
              <p className="text-xs text-neutral-500">
                Subtotal <span data-testid="marketing-subtotal">${subtotal().toFixed(2)}</span> · Shipping <span data-testid="marketing-shipping">${shippingCost().toFixed(2)}</span>
              </p>
              <p className="text-lg font-bold font-heading" data-testid="marketing-price">Total: ${price().toFixed(2)}</p>
            </div>
            <hr className="my-4 border-neutral-100" />
            <p className="text-sm font-semibold mb-3">Shipping address</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-neutral-600">Full name</label>
                <input data-testid="mship-name" required value={ship.name} onChange={setS("name")} className={inputCls} /></div>
              <div><label className="text-xs font-medium text-neutral-600">Email</label>
                <input data-testid="mship-email" type="email" required value={ship.email} onChange={setS("email")} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Address line 1</label>
                <input data-testid="mship-address1" required value={ship.address1} onChange={setS("address1")} className={inputCls} /></div>
              <div><label className="text-xs font-medium text-neutral-600">City</label>
                <input data-testid="mship-city" required value={ship.city} onChange={setS("city")} className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-neutral-600">State</label>
                  <input value={ship.state} onChange={setS("state")} className={inputCls} /></div>
                <div><label className="text-xs font-medium text-neutral-600">ZIP</label>
                  <input data-testid="mship-zip" required value={ship.postal_code} onChange={setS("postal_code")} className={inputCls} /></div>
              </div>
            </div>
            <button data-testid="marketing-checkout-button" disabled={busy}
              className="mt-5 w-full py-2.5 rounded-full text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
              {busy ? "Redirecting to payment..." : `Pay $${price().toFixed(2)} & place order`}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
